import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { MagicWandIcon, RocketIcon } from '@radix-ui/react-icons';

import { useAuthStore } from '@/features/auth/model/auth-store';

import { ROUTES } from '@/shared/config/routes';

const WELCOME_DISMISSED_KEY = 'famecreator_welcome_dismissed';

export const WelcomeModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    // Only show if user hasn't dismissed it before
    const dismissed = localStorage.getItem(WELCOME_DISMISSED_KEY);
    if (dismissed) return;

    // Small delay so the page loads first
    const timer = setTimeout(() => setIsOpen(true), 800);
    return () => clearTimeout(timer);
  }, [isAuthenticated, user]);

  const handleDismiss = useCallback(() => {
    setIsOpen(false);
    localStorage.setItem(WELCOME_DISMISSED_KEY, 'true');
  }, []);

  const handleCreateAI = useCallback(() => {
    handleDismiss();
    navigate(ROUTES.CREATE);
  }, [handleDismiss, navigate]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={handleDismiss}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 animate-in fade-in zoom-in-95 duration-300">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-400 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/25">
            <MagicWandIcon className="w-8 h-8 text-white" />
          </div>
        </div>

        {/* Content */}
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
          Welcome to FameCreator! 🎉
        </h2>
        <p className="text-gray-600 text-center mb-6">
          You're all set. Let's create your first AI — upload a few photos or describe your look, and start generating content in minutes.
        </p>

        {/* Steps preview */}
        <div className="space-y-3 mb-8">
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <div className="w-6 h-6 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-violet-600">1</span>
            </div>
            Upload photos or describe your look
          </div>
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <div className="w-6 h-6 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-violet-600">2</span>
            </div>
            AI builds your personalized model
          </div>
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <div className="w-6 h-6 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-violet-600">3</span>
            </div>
            Generate photos, videos & reels
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <button
            onClick={handleCreateAI}
            className="w-full px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-400 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all flex items-center justify-center gap-2"
          >
            <RocketIcon className="w-4 h-4" />
            Create Your AI
          </button>
          <button
            onClick={handleDismiss}
            className="w-full px-6 py-3 text-gray-500 hover:text-gray-700 text-sm transition-colors"
          >
            I'll explore on my own
          </button>
        </div>
      </div>
    </div>
  );
};
